import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from '../../hooks/use-toast';
import { Wallet, Plus, TrendingDown, TrendingUp, Loader2, Copy, MessageCircle, Clock, CheckCircle2, XCircle, Flame, Upload } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const inr = (n) => '\u20b9' + Number(n || 0).toLocaleString('en-IN');

const AdWallet = ({ userId, user }) => {
  const [wallet, setWallet] = useState(null);
  const [txns, setTxns] = useState([]);
  const [requests, setRequests] = useState([]);
  const [onboarding, setOnboarding] = useState(null);
  const [settings, setSettings] = useState({ upiId: '', whatsappNumber: '' });
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [purpose, setPurpose] = useState('add_fund');
  const [amount, setAmount] = useState('');
  const [utr, setUtr] = useState('');
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    if (!userId) return;
    try {
      const [w, r, s, ob] = await Promise.all([
        axios.get(`${API}/ad-wallet/${userId}`),
        axios.get(`${API}/ads-payment/user/${userId}`),
        axios.get(`${API}/payment-settings`),
        axios.get(`${API}/onboarding/${userId}`).catch(() => ({ data: null })),
      ]);
      setWallet(w.data?.wallet || null);
      setTxns(w.data?.transactions || []);
      setRequests(Array.isArray(r.data) ? r.data : []);
      setSettings({ upiId: s.data?.upiId || 'dropsquadz@okicici', whatsappNumber: s.data?.whatsappNumber || '918819001865' });
      setOnboarding(ob.data || null);
    } catch (e) {
      console.error('ad wallet load error', e);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  const needsOnboardingPay = onboarding && onboarding.adsBudgetStatus === 'Pending Payment';
  const onboardingBudget = onboarding?.adsBudgetSelected || 0;

  const openModal = (p, prefill) => {
    setPurpose(p);
    setAmount(prefill ? String(prefill) : '');
    setUtr('');
    setFile(null);
    setModalOpen(true);
  };

  const submit = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast({ title: 'Enter a valid amount', variant: 'destructive' }); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('userId', userId);
      fd.append('amount', amt);
      fd.append('purpose', purpose);
      fd.append('method', 'UPI-QR');
      if (utr) fd.append('utr', utr);
      if (file) fd.append('screenshot', file);
      await axios.post(`${API}/ads-payment/submit`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast({ title: 'Payment submitted', description: 'Our team will verify and credit your Ad Wallet shortly.' });
      setModalOpen(false);
      await load();
    } catch (e) {
      toast({ title: 'Submission failed', description: e?.response?.data?.detail || 'Try again', variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  const openWhatsapp = () => {
    const phone = (settings.whatsappNumber || '918819001865').replace(/\D/g, '');
    const msg = encodeURIComponent(`Hi DropSquadz! I (Client ID: ${user?.clientId || userId}) paid ${amount ? inr(amount) : ''} for my Ad Wallet. Here is my payment screenshot.`);
    window.open(`https://api.whatsapp.com/send/?phone=${phone}&text=${msg}`, '_blank');
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 text-blue-600 animate-spin" /></div>;

  const balance = wallet?.balance || 0;
  const credited = wallet?.adsCredited || 0;
  const spent = wallet?.adsSpent || 0;
  const spentPct = credited > 0 ? Math.min(100, Math.round((spent / credited) * 100)) : 0;

  return (
    <div className="space-y-5" data-testid="ad-wallet-section">
      {/* Animated balance card */}
      <div className="relative overflow-hidden rounded-3xl p-6 md:p-8 bg-gradient-to-br from-blue-600 via-blue-700 to-cyan-600 text-white shadow-2xl shadow-blue-600/30">
        <div className="absolute -top-10 -right-10 w-48 h-48 bg-white/10 rounded-full blur-2xl animate-pulse" />
        <div className="absolute -bottom-12 -left-12 w-56 h-56 bg-cyan-400/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="relative">
          <div className="flex items-center gap-2 text-blue-100 text-sm font-medium"><Flame className="w-4 h-4" /> Ad Wallet Balance</div>
          <div className="mt-2 text-4xl md:text-5xl font-extrabold tracking-tight">{inr(balance)}</div>
          <p className="text-blue-100 text-sm mt-1">Remaining balance for your ad campaigns</p>

          {/* spend progress */}
          <div className="mt-5">
            <div className="flex justify-between text-xs text-blue-100 mb-1">
              <span>Spent {inr(spent)}</span>
              <span>Added {inr(credited)}</span>
            </div>
            <div className="h-2.5 rounded-full bg-white/20 overflow-hidden">
              <div className="h-full bg-gradient-to-r from-cyan-300 to-white rounded-full transition-all duration-700" style={{ width: `${spentPct}%` }} />
            </div>
          </div>

          <div className="mt-6 flex flex-wrap gap-3">
            <Button onClick={() => openModal('add_fund')} className="bg-white text-blue-700 hover:bg-blue-50 font-semibold rounded-xl" data-testid="add-ads-fund-btn">
              <Plus className="w-4 h-4 mr-1.5" /> Add Ads Fund
            </Button>
            {needsOnboardingPay && (
              <Button onClick={() => openModal('onboarding_ads_budget', onboardingBudget)} className="bg-amber-400 text-amber-950 hover:bg-amber-300 font-semibold rounded-xl animate-pulse" data-testid="pay-onboarding-budget-btn">
                Pay Ads Budget {onboardingBudget ? `(${inr(onboardingBudget)})` : ''}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Stat tiles */}
      <div className="grid grid-cols-2 gap-4">
        <Card className="border-slate-100"><CardContent className="p-4">
          <div className="flex items-center gap-2 text-green-600 text-sm"><TrendingUp className="w-4 h-4" /> Total Added</div>
          <div className="text-2xl font-bold text-slate-800 mt-1">{inr(credited)}</div>
        </CardContent></Card>
        <Card className="border-slate-100"><CardContent className="p-4">
          <div className="flex items-center gap-2 text-red-500 text-sm"><TrendingDown className="w-4 h-4" /> Total Ad Spent</div>
          <div className="text-2xl font-bold text-slate-800 mt-1">{inr(spent)}</div>
        </CardContent></Card>
      </div>

      {/* Pending requests */}
      {requests.length > 0 && (
        <Card className="border-slate-100"><CardContent className="p-4">
          <h3 className="font-semibold text-slate-800 mb-3">Your Fund Requests</h3>
          <div className="space-y-2">
            {requests.map((r) => (
              <div key={r.id} className="flex items-center justify-between bg-slate-50 rounded-xl p-3">
                <div>
                  <div className="font-semibold text-slate-800">{inr(r.amount)}</div>
                  <div className="text-xs text-slate-400">{r.purpose === 'onboarding_ads_budget' ? 'Onboarding Ads Budget' : 'Add Fund'} &middot; {new Date(r.createdAt).toLocaleDateString()}</div>
                </div>
                <span className={`text-xs font-semibold px-3 py-1 rounded-full flex items-center gap-1 ${r.status === 'Approved' ? 'bg-green-100 text-green-700' : r.status === 'Rejected' ? 'bg-red-100 text-red-600' : 'bg-yellow-100 text-yellow-700'}`}>
                  {r.status === 'Approved' ? <CheckCircle2 className="w-3 h-3" /> : r.status === 'Rejected' ? <XCircle className="w-3 h-3" /> : <Clock className="w-3 h-3" />} {r.status}
                </span>
              </div>
            ))}
          </div>
        </CardContent></Card>
      )}

      {/* Transactions */}
      <Card className="border-slate-100"><CardContent className="p-4">
        <h3 className="font-semibold text-slate-800 mb-3">Ad Wallet History</h3>
        {txns.length === 0 ? (
          <p className="text-sm text-slate-400 py-4 text-center">No transactions yet. Add funds to start running ads.</p>
        ) : (
          <div className="space-y-2">
            {txns.map((t) => (
              <div key={t.id} className="flex items-center justify-between border-b border-slate-50 pb-2">
                <div className="flex items-center gap-2">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${t.type === 'credit' ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-500'}`}>
                    {t.type === 'credit' ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                  </div>
                  <div>
                    <div className="text-sm font-medium text-slate-700">{t.note}</div>
                    <div className="text-xs text-slate-400">{new Date(t.createdAt).toLocaleString()}</div>
                  </div>
                </div>
                <div className={`font-bold ${t.type === 'credit' ? 'text-green-600' : 'text-red-500'}`}>{t.type === 'credit' ? '+' : '-'}{inr(t.amount)}</div>
              </div>
            ))}
          </div>
        )}
      </CardContent></Card>

      {/* Add fund modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{purpose === 'onboarding_ads_budget' ? 'Pay Your Ads Budget' : 'Add Ads Fund'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-600">Amount (\u20b9)</label>
              <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="e.g. 1000" className="mt-1 w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-blue-500 outline-none" data-testid="ad-fund-amount" />
            </div>
            <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
              <p className="text-sm text-slate-600 mb-2">Pay to this UPI ID from any app (GPay / PhonePe / Paytm):</p>
              <div className="flex items-center justify-between bg-white rounded-lg px-3 py-2 border border-blue-200">
                <span className="font-bold text-blue-700">{settings.upiId}</span>
                <button onClick={() => { navigator.clipboard?.writeText(settings.upiId); toast({ title: 'UPI ID copied' }); }} className="text-blue-600 text-xs flex items-center gap-1"><Copy className="w-3 h-3" /> Copy</button>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-600">UTR / Reference No. (optional)</label>
              <input value={utr} onChange={(e) => setUtr(e.target.value)} placeholder="12-digit UPI reference" className="mt-1 w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-blue-500 outline-none" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-600 flex items-center gap-1"><Upload className="w-3.5 h-3.5" /> Payment Screenshot (optional)</label>
              <input type="file" accept="image/*" onChange={(e) => setFile(e.target.files?.[0] || null)} className="mt-1 w-full text-sm text-slate-500" />
            </div>
            <div className="flex gap-2">
              <Button onClick={submit} disabled={submitting} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white rounded-xl" data-testid="ad-fund-submit">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Submit for Verification'}
              </Button>
              <Button onClick={openWhatsapp} variant="outline" className="rounded-xl border-green-500 text-green-600 hover:bg-green-50"><MessageCircle className="w-4 h-4" /></Button>
            </div>
            <p className="text-xs text-slate-400 text-center">After payment, our admin verifies and credits your Ad Wallet within a few hours.</p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdWallet;
